/**
 * /market/:slug/regalo/:productSlug — Compra de gift card (checkout + nominación + pago online).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { ArrowLeft, Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  loadMarketCatalog,
  type MarketCatalogResult,
} from "@/lib/market-catalog";
import {
  giftCardTierFromProduct,
  isGiftCardProduct,
  GIFT_CARD_USAGE_DISCLAIMER,
} from "@/lib/gift-card-catalog";
import { GiftCardProductVisual } from "@/components/market/GiftCardProductVisual";
import {
  CHECKOUT_PAYMENT_TARJETA,
  checkoutPaymentLabel,
  isCardPaymentProviderId,
} from "@/lib/storefront-checkout-payments";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { getRequest } from "@tanstack/react-start/server";
import { MarketPdpSkeleton } from "@/components/market/MarketSkeletons";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<MarketCatalogResult> =>
    loadMarketCatalog(slug, { request: getRequest() }),
  );

export const Route = createFileRoute("/market_/$slug/regalo/$productSlug")({
  pendingComponent: MarketPdpSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: GiftCardPurchasePage,
});

function GiftCardPurchasePage() {
  const { slug, productSlug } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;
  const data = result.ok ? result.data : null;
  const product = data?.products.find((p: { slug: string }) => p.slug === productSlug) ?? null;
  const tier = product ? giftCardTierFromProduct(product) : null;

  const [purchaserName, setPurchaserName] = useState("");
  const [purchaserEmail, setPurchaserEmail] = useState("");
  const [purchaserPhone, setPurchaserPhone] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");
  const [paymentMethods, setPaymentMethods] = useState<Array<{ id: string; label: string }>>([]);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void fetch(`/api/market/${slug}/online-payments`)
      .then((r) => r.json())
      .then((d: { methods?: Array<{ id: string; label: string }> }) => {
        const raw = d.methods ?? [];
        // No mostramos el nombre de la pasarela de tarjeta por detrás
        // (Payway/Ualá/Getnet) — se agrupan en un único método "Tarjeta",
        // mismo criterio que ya usa el checkout principal (ver #6).
        const hasCard = raw.some((m) => isCardPaymentProviderId(m.id));
        const list = [
          ...(hasCard ? [{ id: CHECKOUT_PAYMENT_TARJETA, label: checkoutPaymentLabel(CHECKOUT_PAYMENT_TARJETA) }] : []),
          ...raw.filter((m) => !isCardPaymentProviderId(m.id)),
        ];
        setPaymentMethods(list);
        if (list[0]) setPaymentMethod(list[0].id);
      })
      .catch(() => setPaymentMethods([]));
  }, [slug]);

  const offlineScreen = renderMarketPublicOffline(result);
  if (offlineScreen) return offlineScreen;

  if (!result.ok || !data || !product || !tier || !isGiftCardProduct(product)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-muted-foreground">Gift card no disponible</p>
        <Link to="/market/$slug" params={{ slug }} className="text-primary underline">Volver a la tienda</Link>
      </div>
    );
  }

  if (!data.giftCardsEnabled) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <p>Las gift cards no están habilitadas en esta tienda.</p>
        <Link to="/market/$slug" params={{ slug }}>Volver</Link>
      </div>
    );
  }

  // Tokens de theme resueltos server-side vía Experience Engine (Fase D3,
  // mismo patrón validado en D1 — ver ADR-002). Antes se calculaba acá con
  // marketSurfaceStyle(); mismo resultado.
  const brandStyle = data.brand.surfaceStyle;

  const onlineOk = paymentMethods.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onlineOk || !paymentMethod) {
      toast.error("Esta tienda no tiene medios de pago online configurados");
      return;
    }
    if (!purchaserName.trim() || !purchaserEmail.trim() || !recipientName.trim() || !recipientEmail.trim()) {
      toast.error("Completá todos los campos obligatorios");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`/api/market/${slug}/gift-card-purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productId: product.id,
          paymentMethod,
          purchaserName: purchaserName.trim(),
          purchaserEmail: purchaserEmail.trim(),
          purchaserPhone: purchaserPhone.trim() || undefined,
          recipientName: recipientName.trim(),
          recipientEmail: recipientEmail.trim(),
          personalMessage: personalMessage.trim() || undefined,
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.error ?? "No se pudo iniciar la compra");
        return;
      }
      if (d.paymentLink) {
        window.location.href = d.paymentLink;
        return;
      }
      toast.error("No se recibió link de pago");
    } catch {
      toast.error("Error de conexión");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="market-surface min-h-screen pb-12" style={brandStyle}>
      <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-[var(--market-surface-variant)] bg-[var(--market-surface)] px-4">
        <Link to="/market/$slug/p/$productSlug" params={{ slug, productSlug }}
          className="flex h-9 w-9 items-center justify-center rounded-full" aria-label="Volver">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <span className="market-font-display font-bold text-[var(--market-on-surface)]">Regalar gift card</span>
      </header>

      <div className="mx-auto max-w-lg px-4 py-6 space-y-6">
        <GiftCardProductVisual tier={tier} />

        <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl border border-[var(--market-surface-variant)] p-4">
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-[var(--market-on-surface)]">Tus datos</h2>
            <input required className="market-input w-full rounded-lg border px-3 py-2 text-sm" placeholder="Nombre y apellido *"
              value={purchaserName} onChange={(e) => setPurchaserName(e.target.value)} />
            <input required type="email" className="market-input w-full rounded-lg border px-3 py-2 text-sm" placeholder="Email *"
              value={purchaserEmail} onChange={(e) => setPurchaserEmail(e.target.value)} />
            <input className="market-input w-full rounded-lg border px-3 py-2 text-sm" placeholder="Teléfono (opcional)"
              value={purchaserPhone} onChange={(e) => setPurchaserPhone(e.target.value)} />
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-[var(--market-on-surface)]">Para quién es el regalo</h2>
            <input required className="market-input w-full rounded-lg border px-3 py-2 text-sm" placeholder="Nombre del destinatario *"
              value={recipientName} onChange={(e) => setRecipientName(e.target.value)} />
            <input required type="email" className="market-input w-full rounded-lg border px-3 py-2 text-sm" placeholder="Email del destinatario *"
              value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} />
            <textarea className="market-input w-full rounded-lg border px-3 py-2 text-sm min-h-[72px]" placeholder="Mensaje personal (opcional)"
              value={personalMessage} onChange={(e) => setPersonalMessage(e.target.value)} maxLength={280} />
          </section>

          {onlineOk ? (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold">Pago online</h2>
              {paymentMethods.map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="pay"
                    checked={paymentMethod === m.id}
                    onChange={() => setPaymentMethod(m.id)}
                  />
                  {m.label}
                </label>
              ))}
              <p className="text-[11px] text-[var(--market-on-surface-variant)]">
                El pedido queda en espera de cobro hasta que la pasarela confirme el pago. Luego emitimos la gift card y enviamos los emails.
              </p>
            </section>
          ) : (
            <p className="text-sm text-amber-600">La compra online de gift cards no está disponible en este momento. Contactá a la tienda para comprarla por otro medio.</p>
          )}

          <p className="text-[11px] leading-relaxed text-[var(--market-on-surface-variant)]">{GIFT_CARD_USAGE_DISCLAIMER}</p>

          <button
            type="submit"
            disabled={submitting || !onlineOk}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--market-accent)] py-3 text-sm font-bold text-[var(--primary-foreground)] disabled:opacity-50"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
            {onlineOk ? "Ir a pagar" : "Pago online no disponible"}
          </button>
        </form>
      </div>
    </div>
  );
}
